import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette } from '@core/config/constants';
import { RhythmConfig, RHYTHM_DIFFICULTIES } from '@modules/rhythm/config';

/**
 * Minijuego de ritmo: aparecen estrellas que se encogen; el jugador debe tocarlas
 * antes de que desaparezcan. Sirve como referencia de como se estructura un modulo:
 * hereda de BaseScene, usa el perfil compartido y guarda su propio puntaje.
 */
export class RhythmScene extends BaseScene {
  private score = 0;
  private combo = 0;
  private remainingMs: number = RhythmConfig.roundDurationMs;
  private isPlaying = false;

  private spawnIntervalMs: number = RhythmConfig.spawnIntervalMs;
  private noteLifetimeMs: number = RhythmConfig.noteLifetimeMs;

  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private countdownTimer?: Phaser.Time.TimerEvent;

  public constructor() {
    super(RhythmConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.addGradientBackground(0x2a0d4e, 0x6a1f73);
    this.addTwinkles(30);
    this.showDifficultyPicker(RhythmConfig.title, RHYTHM_DIFFICULTIES, (index) => this.beginRound(index));
  }

  private beginRound(difficultyIndex: number): void {
    const difficulty = RHYTHM_DIFFICULTIES[difficultyIndex];
    this.score = 0;
    this.combo = 0;
    this.remainingMs = difficulty.roundDurationMs;
    this.spawnIntervalMs = difficulty.spawnIntervalMs;
    this.noteLifetimeMs = difficulty.noteLifetimeMs;
    this.isPlaying = false;

    this.addBackButton();

    this.scoreText = this.add
      .text(this.centerX, 34, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(0.5, 0);

    this.timerText = this.add
      .text(this.scale.width - 40, 34, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.refreshHud();
    this.startRound();
  }

  private startRound(): void {
    this.isPlaying = true;

    this.spawnTimer = this.time.addEvent({
      delay: this.spawnIntervalMs,
      loop: true,
      callback: () => this.spawnNote(),
    });

    this.countdownTimer = this.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => this.tickCountdown(100),
    });
  }

  private tickCountdown(deltaMs: number): void {
    this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
    this.refreshHud();
    if (this.remainingMs <= 0) {
      this.endRound();
    }
  }

  private spawnNote(): void {
    if (!this.isPlaying) {
      return;
    }

    const margin = RhythmConfig.noteRadius + 20;
    const x = Phaser.Math.Between(margin, this.scale.width - margin);
    const y = Phaser.Math.Between(margin + 80, this.scale.height - margin);

    const note = this.add.circle(x, y, RhythmConfig.noteRadius, Palette.highlight);
    note.setInteractive({ useHandCursor: true });

    const star = this.add.text(x, y, '⭐', { fontSize: '52px' }).setOrigin(0.5);

    const destroyNote = (): void => {
      note.destroy();
      star.destroy();
    };

    note.once(Phaser.Input.Events.POINTER_DOWN, () => {
      this.registerHit();
      this.tweens.add({
        targets: [note, star],
        scale: 1.6,
        alpha: 0,
        duration: 150,
        onComplete: destroyNote,
      });
    });

    this.tweens.add({
      targets: [note, star],
      scale: 0.3,
      duration: this.noteLifetimeMs,
      onComplete: () => {
        if (note.active) {
          this.registerMiss();
          destroyNote();
        }
      },
    });
  }

  private registerHit(): void {
    this.combo += 1;
    this.score += RhythmConfig.pointsPerHit + this.combo * RhythmConfig.comboBonus;
    this.audio.playHit();
    this.refreshHud();
  }

  private registerMiss(): void {
    this.combo = 0;
    this.audio.playMiss();
    this.refreshHud();
  }

  private refreshHud(): void {
    const seconds = Math.ceil(this.remainingMs / 1000);
    this.scoreText.setText(`Puntos: ${this.score}   Combo: x${this.combo}`);
    this.timerText.setText(`⏱ ${seconds}s`);
  }

  private endRound(): void {
    this.isPlaying = false;
    this.spawnTimer?.remove();
    this.countdownTimer?.remove();

    const isRecord = this.profile.submitScore(RhythmConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * RhythmConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.audio.playReward();
    this.showRoundResults({
      title: isRecord ? '¡Nuevo record!' : '¡Bien hecho!',
      score: this.score,
      coins,
    });
  }
}
